/**
 * Parser CSV minimale per l'import competenze dalla pagina admin.
 *
 * Il CSV è interpretato lato client e trasformato in righe
 * `{ name, description?, mission? }` da inviare a `POST /api/competences/import`.
 * Il nome è la chiave: il backend crea i nomi nuovi e aggiorna `description` e
 * `mission` di quelli già presenti.
 *
 * Formato atteso:
 *  - prima riga di intestazione con i nomi delle colonne (case‑insensitive);
 *  - colonna `name` obbligatoria (alias: `nome`, `competenza`);
 *  - colonna opzionale `description` (alias: `descrizione`, `desc`);
 *  - colonna opzionale `mission` (alias: `missione`, `scopo`);
 *  - colonne opzionali `level1`..`level5` (alias: `livello1`..`livello5`): i
 *    testi esplicativi dei Livelli di proficiency e-CF;
 *  - colonna opzionale `area` (alias: `area e-cf`, `dimensione`): valore tra
 *    `Plan`, `Build`, `Run`, `Enable`, `Manage` (case‑insensitive); i valori
 *    non riconosciuti vengono ignorati e l'area sarà derivata lato backend dal
 *    prefisso e-CF del nome;
 *  - separatore virgola `,`; supporto dei campi tra virgolette doppie con
 *    escape a virgolette raddoppiate (`"a""b"` → `a"b`).
 *
 * Riusa lo stesso stile del parser utenti (`parseUsersCsv`): non è un parser
 * CSV completo ma copre gli export tipici da fogli di calcolo.
 */
import {
  COMPETENCE_AREA_VALUES,
  type CompetenceArea,
  type ImportCompetenceRow,
} from '../shared/dto';

/** Errore sollevato quando il contenuto CSV non è utilizzabile. */
export class CsvParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CsvParseError';
  }
}

/** Alias di intestazione accettati per ciascun campo (normalizzati lowercase). */
const HEADER_ALIASES: Record<
  | 'name'
  | 'description'
  | 'mission'
  | 'area'
  | 'level1'
  | 'level2'
  | 'level3'
  | 'level4'
  | 'level5',
  readonly string[]
> = {
  name: ['name', 'nome', 'competenza', 'competenza name'],
  description: ['description', 'descrizione', 'desc'],
  mission: ['mission', 'missione', 'scopo'],
  area: ['area', 'area e-cf', 'area ecf', 'dimensione', 'dimension'],
  level1: ['level1', 'livello1', 'livello 1'],
  level2: ['level2', 'livello2', 'livello 2'],
  level3: ['level3', 'livello3', 'livello 3'],
  level4: ['level4', 'livello4', 'livello 4'],
  level5: ['level5', 'livello5', 'livello 5'],
};

/**
 * Normalizza il testo grezzo della colonna `area` in un valore
 * {@link CompetenceArea} valido (case‑insensitive), oppure `undefined` se il
 * valore è vuoto o non riconosciuto (in tal caso l'area sarà derivata lato
 * backend dal prefisso e-CF del nome).
 */
function normalizeArea(raw: string): CompetenceArea | undefined {
  const normalized = raw.trim().toLowerCase();
  if (normalized === '') return undefined;
  return COMPETENCE_AREA_VALUES.find((value) => value.toLowerCase() === normalized);
}

/** Scompone una singola riga CSV in campi, gestendo le virgolette doppie. */
function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1; // salta la virgoletta di escape
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

/** Individua l'indice della colonna corrispondente a un campo, o -1. */
function findColumn(header: string[], field: keyof typeof HEADER_ALIASES): number {
  const aliases = HEADER_ALIASES[field];
  return header.findIndex((h) => aliases.includes(h.trim().toLowerCase()));
}

/**
 * Interpreta il testo CSV in un elenco di righe competenza pronte per l'import.
 *
 * @throws {CsvParseError} se il file è vuoto, manca la colonna `name`, o non
 *   contiene righe dati valide.
 */
export function parseCompetencesCsv(text: string): ImportCompetenceRow[] {
  const lines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((l) => l.trim() !== '');

  if (lines.length === 0) {
    throw new CsvParseError('Il file CSV è vuoto.');
  }

  const header = splitCsvLine(lines[0]);
  const nameIdx = findColumn(header, 'name');
  if (nameIdx === -1) {
    throw new CsvParseError('Colonna "name" mancante nell\'intestazione del CSV.');
  }
  const descIdx = findColumn(header, 'description');
  const missionIdx = findColumn(header, 'mission');
  const areaIdx = findColumn(header, 'area');
  const levelIdx: Record<'level1' | 'level2' | 'level3' | 'level4' | 'level5', number> = {
    level1: findColumn(header, 'level1'),
    level2: findColumn(header, 'level2'),
    level3: findColumn(header, 'level3'),
    level4: findColumn(header, 'level4'),
    level5: findColumn(header, 'level5'),
  };

  const rows: ImportCompetenceRow[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = splitCsvLine(lines[i]);
    const name = (cells[nameIdx] ?? '').trim();
    if (name === '') continue; // salta righe senza nome

    const row: ImportCompetenceRow = { name };
    if (descIdx !== -1) {
      const description = (cells[descIdx] ?? '').trim();
      if (description !== '') row.description = description;
    }
    if (missionIdx !== -1) {
      const mission = (cells[missionIdx] ?? '').trim();
      if (mission !== '') row.mission = mission;
    }
    if (areaIdx !== -1) {
      const area = normalizeArea(cells[areaIdx] ?? '');
      if (area !== undefined) row.area = area;
    }
    // Testi dei Livelli di proficiency e-CF: letti solo se la colonna è
    // presente e la cella non è vuota (come description/mission).
    for (const key of ['level1', 'level2', 'level3', 'level4', 'level5'] as const) {
      const idx = levelIdx[key];
      if (idx !== -1) {
        const value = (cells[idx] ?? '').trim();
        if (value !== '') row[key] = value;
      }
    }
    rows.push(row);
  }

  if (rows.length === 0) {
    throw new CsvParseError('Nessuna riga competenza valida nel CSV.');
  }

  return rows;
}
